import { useState, useEffect, type KeyboardEvent } from 'react';
import { Box, Drawer, Typography, Button, Select, MenuItem, FormControl, InputLabel } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import SyncIcon from '@mui/icons-material/Sync';
import FilterMultiSelect, { GenericOption } from './FilterMultiSelect';
import FilterChip from './FilterChip';
import { AdvancedFilterDrawerAzureProps, FilterStateAzure } from './types';
import { TIME_RANGE_OPTIONS, TimeRangeValue, DEFAULT_FILTERS_AZURE } from './constants';

const AdvancedFilterDrawerAzure = ({
  open,
  onClose,
  onApply,
  onSaveDefault,
  onResetDefault,
  currentFilters,
  filterOptions
}: AdvancedFilterDrawerAzureProps) => {
  const theme = useTheme();
  const [filters, setFilters] = useState<FilterStateAzure>(currentFilters);

  // Sync filters when drawer opens with current applied filters
  useEffect(() => {
    if (open) {
      setFilters(currentFilters);
    }
  }, [open, currentFilters]);

  const toOptions = (values: string[]): GenericOption[] => {
    return values.map(v => ({ label: v, value: v }));
  };


  const buildFiltersForSubmit = (): FilterStateAzure => {
    const sessionTimeRange = sessionStorage.getItem('unifiedAlertsDateRangeType') as FilterStateAzure['timeRange'] | null;
    const selectedTimeRange = sessionTimeRange || filters.timeRange;
    const startDate = sessionStorage.getItem('unifiedAlertsStartDate');
    const endDate = sessionStorage.getItem('unifiedAlertsEndDate');

    if (selectedTimeRange === 'custom' && startDate && endDate) {
      return {
        ...filters,
        timeRange: 'custom',
        startDate,
        endDate,
      };
    }

    return {
      ...filters,
      timeRange: selectedTimeRange,
      startDate: undefined,
      endDate: undefined,
    };
  };

  const handleApply = () => {
    onApply(buildFiltersForSubmit());
    onClose();
  };

  const handleSaveDefault = () => {
    onSaveDefault(buildFiltersForSubmit());
    onClose();
  }

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      handleApply();
    }
  };

  const handleReset = () => {
    setFilters(DEFAULT_FILTERS_AZURE);
    onApply(DEFAULT_FILTERS_AZURE);
  };

  const handleResetDefault = () => {
    onResetDefault(DEFAULT_FILTERS_AZURE);

    setFilters(DEFAULT_FILTERS_AZURE);
    onApply(DEFAULT_FILTERS_AZURE);

    onClose();
  }

  const handleClose = () => {
    // Discard temp changes
    setFilters(currentFilters);
    onClose();
  };

  const renderFilterChips = (items: GenericOption[], onDelete: (value: string) => void) => {
    const filteredItems = items.filter(item => item.value !== 'all');
    if (filteredItems.length === 0) return null;

    return (
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0, mt: 1 }}>
        {filteredItems.map((item) => (
          <FilterChip
            key={item.value}
            label={item.label}
            value={item.value}
            onDelete={onDelete}
          />
        ))}
      </Box>
    );
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={handleClose}
      variant="persistent"
      PaperProps={{
        sx: {
          width: 490,
          maxWidth: 490,
          backgroundColor: '#FFFFFF',
          height: 'calc(100vh - 76px)',
          top: '76px',
          overflowY: 'auto',
          boxShadow: '-2px 4px 8px rgba(0, 0, 0, 0.1)',
        }
      }}
      ModalProps={{
        BackdropProps: {
          sx: {
            backgroundColor: 'rgba(255, 255, 255, 0.5)',
          }
        }
      }}
    >
      <Box
        sx={{ display: 'flex', flexDirection: 'column', height: '100%', backgroundColor: '#FFFFFF' }}
        onKeyDown={handleKeyDown}
      >
        {/* Header */}
        <Box sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          px: 3,
          py: 2.5,
          borderBottom: `1px solid ${theme.palette.grey[300]}`
        }}>
          <Typography variant="h5" sx={{ fontWeight: 600, color: 'primary.main' }}>
            Advance Filter
          </Typography>
          <Button
            variant="text"
            onClick={handleReset}
            startIcon={<SyncIcon sx={{ fontSize: '1rem' }} />}
            sx={{
              color: 'primary.main',
              textTransform: 'uppercase',
              fontWeight: 600,
              fontSize: '0.75rem'
            }}
          >
            RESET FILTERS
          </Button>
        </Box>

        <Box sx={{
          display: 'flex',
          justifyContent: 'space-between',
          gap: 2,
          marginTop: '10px',
          paddingLeft: '2vh',
          paddingRight: '5vh'
        }}>
          <Button
            variant="text"
            onClick={handleResetDefault}
            startIcon={<SyncIcon sx={{ fontSize: '1rem' }} />}
            sx={{
              color: 'primary.main',
              textTransform: 'uppercase',
              fontWeight: 600,
              fontSize: '0.75rem'
            }}
          >
            RESET DEFAULT FILTERS
          </Button>
          <Button
            variant="outlined"
            color="primary"
            onClick={handleSaveDefault}
            sx={{
              textTransform: 'uppercase',
              fontWeight: 600
            }}
          >
            SAVE AS DEFAULT FILTERS
          </Button>
        </Box>

        {/* Body */}
        <Box sx={{ flex: 1, overflowY: 'auto', px: 3, py: 2, backgroundColor: '#FFFFFF' }}>
          {/* Time Range */}
          <Box sx={{ mb: 3 }}>
            <FormControl fullWidth>
              <InputLabel
                sx={{
                  color: '#9CA3AF !important',
                  fontWeight: 400,
                  backgroundColor: 'white',
                  paddingX: '4px',
                  marginLeft: '-2px',
                  '&.Mui-focused': {
                    color: '#333333 !important',
                    backgroundColor: 'white'
                  },
                  '&.MuiInputLabel-shrink': {
                    color: '#333333 !important',
                    backgroundColor: 'white',
                    paddingX: '4px'
                  }
                }}
              >
                Time Range
              </InputLabel>
              <Select
                value={filters.timeRange}
                onChange={(e) => {
                  const value = e.target.value as TimeRangeValue;

                  setFilters(prev => ({
                    ...prev,
                    timeRange: value,
                    startDate: undefined,
                    endDate: undefined,
                  }));

                  if (value !== 'custom') {
                    sessionStorage.removeItem('unifiedAlertsStartDate');
                    sessionStorage.removeItem('unifiedAlertsEndDate');
                  }
                }}
                label="Time Range"
                IconComponent={KeyboardArrowDownIcon}
                MenuProps={{
                  PaperProps: {
                    sx: {
                      backgroundColor: 'white !important',
                      width: '442px',
                      '& .MuiMenuItem-root': {
                        backgroundColor: 'white !important',
                        color: '#333333 !important',
                        '&:hover': {
                          backgroundColor: '#E6F6FD !important'
                        },
                        '&.Mui-selected': {
                          backgroundColor: '#E6F6FD !important',
                          '&:hover': {
                            backgroundColor: '#E6F6FD !important'
                          }
                        }
                      },
                      '& .MuiList-root': {
                        backgroundColor: 'white !important'
                      }
                    }
                  }
                }}
                sx={{
                  height: '50px',
                  backgroundColor: 'white',
                  '& .MuiSelect-select': {
                    color: 'text.primary',
                    paddingTop: '14px',
                    paddingBottom: '14px'
                  },
                  '& .MuiSelect-icon': {
                    color: 'primary.main'
                  },
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#D1D5DB'
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'primary.main'
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'primary.main'
                  }
                }}
              >
                {TIME_RANGE_OPTIONS.map(option => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          {/* Severity */}
          <Box sx={{ mb: 3 }}>
            <FilterMultiSelect
              label="Severity"
              placeholder="Select severities..."
              options={toOptions(filterOptions.severity || [])}
              value={filters.mappedSeverity}
              onChange={(newValue) => setFilters(prev => ({ ...prev, mappedSeverity: newValue }))}
              width={442}
              pluralLabel="Severities"
            />
            {renderFilterChips(filters.mappedSeverity, (value) =>
              setFilters(prev => ({ ...prev, mappedSeverity: prev.mappedSeverity.filter(s => s.value !== value) }))
            )}
          </Box>

          {/* Status */}
          <Box sx={{ mb: 3 }}>
            <FilterMultiSelect
              label="Status"
              placeholder="Select statuses..."
              options={toOptions(filterOptions.status || [])}
              value={filters.status}
              onChange={(newValue) => setFilters(prev => ({ ...prev, status: newValue }))}
              width={442}
              pluralLabel="Statuses"
            />
            {renderFilterChips(filters.status, (value) =>
              setFilters(prev => ({ ...prev, status: prev.status.filter(s => s.value !== value) }))
            )}
          </Box>

          {/* Monitoring Services */}
          <Box sx={{ mb: 3 }}>
            <FilterMultiSelect
              label="Monitoring Services"
              placeholder="Select monitoring services..."
              options={toOptions(filterOptions.monitoringServices || [])}
              value={filters.monitoringServices}
              onChange={(newValue) => setFilters(prev => ({ ...prev, monitoringServices: newValue }))}
              width={442}
              pluralLabel="Monitoring Services"
            />
            {renderFilterChips(filters.monitoringServices, (value) =>
              setFilters(prev => ({ ...prev, monitoringServices: prev.monitoringServices.filter(s => s.value !== value) }))
            )}
          </Box>

          {/* Subscription */}
          <Box sx={{ mb: 3 }}>
            <FilterMultiSelect
              label="Subscription"
              placeholder="Select subscriptions..."
              options={toOptions(filterOptions.subscriptions || [])}
              value={filters.subscriptions}
              onChange={(newValue) => setFilters(prev => ({ ...prev, subscriptions: newValue }))}
              width={442}
              pluralLabel="Subscriptions"
            />
            {renderFilterChips(filters.subscriptions, (value) =>
              setFilters(prev => ({ ...prev, subscriptions: prev.subscriptions.filter(s => s.value !== value) }))
            )}
          </Box>

          {/* Resource Groups */}
          <Box sx={{ mb: 3 }}>
            <FilterMultiSelect
              label="Resource Groups"
              placeholder="Select resource groups..."
              options={toOptions(filterOptions.resourceGroups || [])}
              value={filters.resourceGroups}
              onChange={(newValue) => setFilters(prev => ({ ...prev, resourceGroups: newValue }))}
              width={442}
              pluralLabel="Resource Groups"
            />
            {renderFilterChips(filters.resourceGroups, (value) =>
              setFilters(prev => ({ ...prev, resourceGroups: prev.resourceGroups.filter(s => s.value !== value) }))
            )}
          </Box>

          {/* Resource Types */}
          <Box sx={{ mb: 3 }}>
            <FilterMultiSelect
              label="Resource Types"
              placeholder="Select resource types..."
              options={toOptions(filterOptions.resourceTypes || [])}
              value={filters.resourceTypes}
              onChange={(newValue) => setFilters(prev => ({ ...prev, resourceTypes: newValue }))}
              width={442}
              pluralLabel="Resource Types"
            />
            {renderFilterChips(filters.resourceTypes, (value) =>
              setFilters(prev => ({ ...prev, resourceTypes: prev.resourceTypes.filter(s => s.value !== value) }))
            )}
          </Box>

          {/* Alert Types */}
          <Box sx={{ mb: 3 }}>
            <FilterMultiSelect
              label="Alert Types"
              placeholder="Select alert types..."
              options={toOptions(filterOptions.signalTypes || [])}
              value={filters.signalTypes}
              onChange={(newValue) => setFilters(prev => ({ ...prev, signalTypes: newValue }))}
              width={442}
              pluralLabel="Alert Types"
            />
            {renderFilterChips(filters.signalTypes, (value) =>
              setFilters(prev => ({ ...prev, signalTypes: prev.signalTypes.filter(s => s.value !== value) }))
            )}
          </Box>

        </Box>

        {/* Footer */}
        <Box sx={{
          px: 3,
          py: 1.5,
          borderTop: `1px solid ${theme.palette.grey[300]}`,
          display: 'flex',
          justifyContent: 'flex-end',
          gap: 2
        }}>
          <Button
            variant="outlined"
            color="secondary"
            onClick={onClose}
            sx={{
              textTransform: 'uppercase',
              px: 4,
              py: 1.5,
              fontWeight: 600
            }}
          >
            CLOSE
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleApply}
            sx={{
              textTransform: 'uppercase',
              px: 4,
              py: 1.5,
              fontWeight: 600
            }}
          >
            APPLY FILTERS
          </Button>
        </Box>
      </Box>
    </Drawer>
  );
};

export default AdvancedFilterDrawerAzure;